import {
  Component,
  Input,
  Output,
  EventEmitter,
  booleanAttribute,
  OnInit,
  OnDestroy,
  DoCheck,
  OnChanges,
  SimpleChanges,
  DestroyRef,
  inject,
} from '@angular/core';
import { WidgetContentComponent } from '../widget-content/widget-content.component';
import { Position } from '../models/position';
import { ResizeDirection } from '../models/resize-direction';
import { GridAddress } from '../models/grid-address';
import { Widget } from '../models/widget';
import { fromEvent, Subscription, take, takeUntil } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
  selector: 'via-widget-container',
  standalone: true,
  imports: [WidgetContentComponent],
  templateUrl: './widget-container.component.html',
  styleUrl: './widget-container.component.scss',
})
export class WidgetContainerComponent
  implements OnInit, OnDestroy, OnChanges, DoCheck
{
  // ============ Configuration Constants ============
  readonly gridInset = 8;
  readonly defaultCardWidth = 300;
  readonly defaultCardHeight = 200;

  // ============ Input/Output ============
  @Input() isLiveData = false;
  @Input({ transform: booleanAttribute }) isEditMode = false;
  @Input() gridSize = 50;
  @Input() widgetConfig: Widget;
  @Input() draggable = true;
  @Input() resizable = true;

  @Output() closed = new EventEmitter<void>();
  @Output() widgetSnapped = new EventEmitter<Position>();
  @Output() widgetPositionChanged = new EventEmitter<GridAddress>();
  @Output() widgetPreviewAddressChanged = new EventEmitter<GridAddress | null>();
  @Output() widgetDragStart = new EventEmitter<void>();
  @Output() widgetDragEnd = new EventEmitter<void>();

  // ============ Component Properties ============
  cardWidth = this.defaultCardWidth;
  cardHeight = this.defaultCardHeight;

  position: Position = { x: 100, y: 100 };
  isDragging = false;
  isSnapped = false;
  isResizing = false;
  activeResizeDirection: ResizeDirection | null = null;
  // ============ Private State ============
  private dragOffset: Position = { x: 0, y: 0 };
  private resizeDirection: ResizeDirection | null = null;
  private initialMousePos: Position = { x: 0, y: 0 };
  private initialCardAddress: GridAddress | null = null;
  private pendingResizeAddress: GridAddress | null = null;
  private snappedStateTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastAppliedGridAddressKey: string | null = null;
  private readonly destroyRef = inject(DestroyRef);
  private activeDocumentMouseSubscription: Subscription | null = null;
  private selectionGuardSubscription: Subscription | null = null;

  // ============ Angular Lifecycle ============
  ngOnInit() {
    this.syncFromGridAddress(true);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['widgetConfig'] || changes['gridSize']) {
      this.syncFromGridAddress(true);
    }
  }

  ngDoCheck() {
    // Parent can mutate gridAddress in-place, so track by value.
    this.syncFromGridAddress();
  }

  ngOnDestroy() {
    this.clearSnappedStateTimeout();
    this.stopDocumentMouseTracking();
    this.setSelectionGuard(false);
  }

  get canCloseWidget(): boolean {
    return this.isEditMode === true && this.widgetConfig?.showCloseButton !== false;
  }

  // ============ Public Event Handlers ============
  onCloseClick(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.isEditMode !== true) {
      return;
    }

    this.closed.emit();
  }

  onMouseDown(event: MouseEvent) {
    if (!this.draggable) {
      return;
    }

    const target = event.target as HTMLElement;
    if (this.isInteractiveTarget(target)) {
      return;
    }

    this.isDragging = true;
    this.clearSnappedState();
    this.widgetDragStart.emit();
    this.widgetPreviewAddressChanged.emit(this.positionToGridAddress());

    const relativeMousePosition = this.getRelativeMousePosition(event);
    this.dragOffset = {
      x: relativeMousePosition.x - this.position.x,
      y: relativeMousePosition.y - this.position.y,
    };

    this.setSelectionGuard(true);
    this.startDocumentMouseTracking();
    event.preventDefault();
    event.stopPropagation();
  }

  onResizeStart(event: MouseEvent, direction: ResizeDirection) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.resizable) {
      return;
    }

    this.isResizing = true;
    this.widgetDragStart.emit();
    this.resizeDirection = direction;
    this.activeResizeDirection = direction;
    this.pendingResizeAddress = null;
    this.initialMousePos = this.getRelativeMousePosition(event);
    this.initialCardAddress = this.positionToGridAddress();

    this.setSelectionGuard(true);
    this.startDocumentMouseTracking();
  }

  onCardClick(event: Event) {
    if (this.isDragging) {
      return;
    }

    const target = event.target as HTMLElement;
    if (this.isInteractiveTarget(target)) {
      return;
    }
  }

  snapToGrid(gridX: number, gridY: number) {
    const snapped = this.getSnappedPosition(
      gridX + this.gridInset,
      gridY + this.gridInset,
    );

    this.position = snapped;
    this.showTemporarySnappedState();
    this.isDragging = false;
    this.widgetPreviewAddressChanged.emit(this.positionToGridAddress());
    this.widgetSnapped.emit(this.position);
  }

  // ============ Document Mouse Streams ============
  private startDocumentMouseTracking(): void {
    this.stopDocumentMouseTracking();

    const documentMouseMove$ = fromEvent<MouseEvent>(document, 'mousemove');
    const documentMouseUp$ = fromEvent<MouseEvent>(document, 'mouseup');

    const mouseMoveSubscription = documentMouseMove$
      .pipe(
        takeUntil(documentMouseUp$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.onDocumentMouseMove(event));

    const mouseUpSubscription = documentMouseUp$
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.onDocumentMouseUp(event);
        this.stopDocumentMouseTracking();
      });

    this.activeDocumentMouseSubscription = new Subscription();
    this.activeDocumentMouseSubscription.add(mouseMoveSubscription);
    this.activeDocumentMouseSubscription.add(mouseUpSubscription);
  }

  private stopDocumentMouseTracking(): void {
    this.activeDocumentMouseSubscription?.unsubscribe();
    this.activeDocumentMouseSubscription = null;
  }

  private onDocumentMouseMove(event: MouseEvent): void {
    if (this.draggable && this.isDragging && !this.isResizing) {
      const relativeMousePosition = this.getRelativeMousePosition(event);

      let newPosition = {
        x: relativeMousePosition.x - this.dragOffset.x,
        y: relativeMousePosition.y - this.dragOffset.y,
      };

      newPosition = this.constrainToContainer(newPosition);
      this.position = newPosition;
      this.widgetPreviewAddressChanged.emit(this.positionToGridAddress());
      return;
    }

    if (this.resizable && this.isResizing) {
      this.handleResize(event);
    }
  }

  private onDocumentMouseUp(_event: MouseEvent): void {
    if (this.draggable && this.isDragging && !this.isResizing) {
      this.isDragging = false;
      this.widgetDragEnd.emit();

      const snappedPosition = this.getSnappedPosition(
        this.position.x,
        this.position.y,
      );
      this.position = snappedPosition;
      this.showTemporarySnappedState();
      this.widgetSnapped.emit(snappedPosition);
      this.widgetPreviewAddressChanged.emit(null);

      this.setSelectionGuard(false);
      return;
    }

    if (this.isResizing) {
      this.finishResize();
    }
  }

  // ============ Private Lifecycle Helpers ============
  private syncFromGridAddress(force = false) {
    const gridAddress = this.widgetConfig?.gridAddress;
    const nextKey = this.getGridAddressKey(gridAddress);

    if (!gridAddress || (!force && nextKey === this.lastAppliedGridAddressKey)) {
      return;
    }

    this.lastAppliedGridAddressKey = nextKey;
    this.position = this.gridAddressToPosition(gridAddress);

    const dims = this.getCardDimensionsFromAddress(gridAddress);
    this.cardWidth = dims.width;
    this.cardHeight = dims.height;
  }

  // ============ Private Drag/Resize Helpers ============
  private handleResize(event: MouseEvent) {
    if (!this.initialCardAddress || !this.resizeDirection) {
      return;
    }

    const relativeMousePosition = this.getRelativeMousePosition(event);
    const deltaCols = Math.round(
      (relativeMousePosition.x - this.initialMousePos.x) / this.gridSize,
    );
    const deltaRows = Math.round(
      (relativeMousePosition.y - this.initialMousePos.y) / this.gridSize,
    );

    const newAddress = this.calculateNewAddressFromMousePosition(
      this.initialCardAddress,
      this.resizeDirection,
      deltaCols,
      deltaRows,
    );

    this.pendingResizeAddress = newAddress;
    this.updateCardFromAddress(newAddress);
    this.widgetPreviewAddressChanged.emit(newAddress);
  }

  private finishResize() {
    const finalAddress = this.pendingResizeAddress;

    this.isResizing = false;
    this.resizeDirection = null;
    this.activeResizeDirection = null;
    this.initialCardAddress = null;
    this.pendingResizeAddress = null;
    this.widgetDragEnd.emit();
    this.widgetPreviewAddressChanged.emit(null);
    this.setSelectionGuard(false);

    if (finalAddress) {
      this.widgetPositionChanged.emit(finalAddress);
    }
  }

  // ============ Private Widget Address Helpers ============
  private getGridAddressKey(address?: GridAddress): string {
    if (!address) {
      return '';
    }

    return `${address.topLeft.column}${address.topLeft.row}-${address.bottomRight.column}${address.bottomRight.row}`;
  }

  private updateCardFromAddress(address: GridAddress) {
    this.position = this.gridAddressToPosition(address);

    const newDimensions = this.getCardDimensionsFromAddress(address);
    this.cardWidth = newDimensions.width;
    this.cardHeight = newDimensions.height;
  }

  private showTemporarySnappedState(durationMs = 250): void {
    this.isSnapped = true;
    this.clearSnappedStateTimeout();
    this.snappedStateTimeout = setTimeout(() => {
      this.isSnapped = false;
      this.snappedStateTimeout = null;
    }, durationMs);
  }

  private clearSnappedState(): void {
    this.isSnapped = false;
    this.clearSnappedStateTimeout();
  }

  private clearSnappedStateTimeout(): void {
    if (this.snappedStateTimeout) {
      clearTimeout(this.snappedStateTimeout);
      this.snappedStateTimeout = null;
    }
  }

  // ============ Private Resize Addressing ============
  private calculateNewAddressFromMousePosition(
    originalAddress: GridAddress,
    direction: ResizeDirection,
    deltaCols: number,
    deltaRows: number,
  ): GridAddress {
    const dimensions = this.getCurrentContainerDimensions();
    const maxCol = Math.floor(dimensions.width / this.gridSize) - 1;
    const maxRow = Math.floor(dimensions.height / this.gridSize);

    const leftCol = this.getColumnIndex(originalAddress.topLeft.column);
    const rightCol = this.getColumnIndex(originalAddress.bottomRight.column);
    const topRow = originalAddress.topLeft.row;
    const bottomRow = originalAddress.bottomRight.row;

    let nextLeftCol = leftCol;
    let nextRightCol = rightCol;
    let nextTopRow = topRow;
    let nextBottomRow = bottomRow;

    switch (direction) {
      case 'right':
        nextRightCol = rightCol + deltaCols;
        break;
      case 'left':
        nextLeftCol = leftCol + deltaCols;
        break;
      case 'bottom':
        nextBottomRow = bottomRow + deltaRows;
        break;
      case 'top':
        nextTopRow = topRow + deltaRows;
        break;
      case 'bottom-right':
        nextRightCol = rightCol + deltaCols;
        nextBottomRow = bottomRow + deltaRows;
        break;
      case 'bottom-left':
        nextLeftCol = leftCol + deltaCols;
        nextBottomRow = bottomRow + deltaRows;
        break;
      case 'top-right':
        nextRightCol = rightCol + deltaCols;
        nextTopRow = topRow + deltaRows;
        break;
      case 'top-left':
        nextLeftCol = leftCol + deltaCols;
        nextTopRow = topRow + deltaRows;
        break;
    }

    nextLeftCol = Math.min(nextLeftCol, nextRightCol);
    nextRightCol = Math.max(nextRightCol, nextLeftCol);
    nextTopRow = Math.min(nextTopRow, nextBottomRow);
    nextBottomRow = Math.max(nextBottomRow, nextTopRow);

    nextLeftCol = Math.max(0, Math.min(nextLeftCol, maxCol));
    nextRightCol = Math.max(0, Math.min(nextRightCol, maxCol));
    nextTopRow = Math.max(1, Math.min(nextTopRow, maxRow));
    nextBottomRow = Math.max(1, Math.min(nextBottomRow, maxRow));

    if (nextRightCol < nextLeftCol) {
      if (direction.includes('left')) {
        nextLeftCol = nextRightCol;
      } else {
        nextRightCol = nextLeftCol;
      }
    }

    if (nextBottomRow < nextTopRow) {
      if (direction.includes('top')) {
        nextTopRow = nextBottomRow;
      } else {
        nextBottomRow = nextTopRow;
      }
    }

    return {
      topLeft: {
        column: this.getGridColumnLetter(nextLeftCol),
        row: nextTopRow,
      },
      bottomRight: {
        column: this.getGridColumnLetter(nextRightCol),
        row: nextBottomRow,
      },
    };
  }

  // ============ Private Grid Conversion Helpers ============
  private positionToGridAddress(): GridAddress {
    const totalWidth = this.cardWidth + this.gridInset * 2;
    const totalHeight = this.cardHeight + this.gridInset * 2;
    const cellsWide = Math.ceil(totalWidth / this.gridSize);
    const cellsHigh = Math.ceil(totalHeight / this.gridSize);

    const startCol = Math.round(
      (this.position.x - this.gridInset) / this.gridSize,
    );
    const startRow =
      Math.round((this.position.y - this.gridInset) / this.gridSize) + 1;

    return {
      topLeft: { column: this.getGridColumnLetter(startCol), row: startRow },
      bottomRight: {
        column: this.getGridColumnLetter(startCol + cellsWide - 1),
        row: startRow + cellsHigh - 1,
      },
    };
  }

  private gridAddressToPosition(address: GridAddress): Position {
    const startCol = this.getColumnIndex(address.topLeft.column);
    const startRow = address.topLeft.row - 1;

    return {
      x: startCol * this.gridSize + this.gridInset,
      y: startRow * this.gridSize + this.gridInset,
    };
  }

  private getCardDimensionsFromAddress(address: GridAddress): {
    width: number;
    height: number;
  } {
    const cellSpanWidth =
      (this.getColumnIndex(address.bottomRight.column) -
        this.getColumnIndex(address.topLeft.column) +
        1) *
      this.gridSize;
    const cellSpanHeight =
      (address.bottomRight.row - address.topLeft.row + 1) * this.gridSize;
    const width = Math.max(1, cellSpanWidth - this.gridInset * 2);
    const height = Math.max(1, cellSpanHeight - this.gridInset * 2);
    return { width, height };
  }

  private getGridColumnLetter(index: number): string {
    return String.fromCharCode(65 + Math.max(0, index));
  }

  private getColumnIndex(letter: string): number {
    return letter.charCodeAt(0) - 65;
  }

  // ============ Private DOM and Selection Helpers ============
  private isInteractiveTarget(target: HTMLElement): boolean {
    return (
      target.classList.contains('floating-card-close') ||
      !!target.closest('.floating-card-close') ||
      !!target.closest('button') ||
      !!target.closest('input') ||
      !!target.closest('textarea')
    );
  }

  private getRelativeMousePosition(event: MouseEvent): Position {
    const containerRect = this.getContainerRect();
    return {
      x: event.clientX - containerRect.left,
      y: event.clientY - containerRect.top,
    };
  }

  private getContainerRect(): DOMRect {
    const containerElement = document.querySelector('.dashboard-container');
    if (containerElement) {
      return containerElement.getBoundingClientRect();
    }

    const gridElement = document.querySelector('.dashboard-grid');
    if (gridElement) {
      return gridElement.getBoundingClientRect();
    }

    return new DOMRect();
  }

  private setSelectionGuard(enabled: boolean): void {
    this.selectionGuardSubscription?.unsubscribe();
    this.selectionGuardSubscription = null;

    if (!enabled) {
      return;
    }

    this.selectionGuardSubscription = fromEvent<Event>(document, 'selectstart')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => event.preventDefault());
  }

  // ============ Private Positioning Helpers ============
  private constrainToContainer(position: Position): Position {
    const dimensions = this.getCurrentContainerDimensions();
    if (dimensions.width <= 0 || dimensions.height <= 0) {
      return {
        x: Math.max(0, position.x),
        y: Math.max(0, position.y),
      };
    }

    const minX = this.gridInset;
    const minY = this.gridInset;
    const maxX = dimensions.width - this.cardWidth - this.gridInset;
    const maxY = dimensions.height - this.cardHeight - this.gridInset;

    return {
      x: Math.max(minX, Math.min(position.x, maxX)),
      y: Math.max(minY, Math.min(position.y, maxY)),
    };
  }

  private getSnappedPosition(x: number, y: number): Position {
    const safeX = Math.max(this.gridInset, x);
    const safeY = Math.max(this.gridInset, y);
    const dimensions = this.getCurrentContainerDimensions();

    let snappedX =
      Math.round((safeX - this.gridInset) / this.gridSize) * this.gridSize +
      this.gridInset;
    let snappedY =
      Math.round((safeY - this.gridInset) / this.gridSize) * this.gridSize +
      this.gridInset;

    if (dimensions.width <= 0 || dimensions.height <= 0) {
      return { x: snappedX, y: snappedY };
    }

    snappedX = Math.max(
      this.gridInset,
      Math.min(snappedX, dimensions.width - this.cardWidth - this.gridInset),
    );
    snappedY = Math.max(
      this.gridInset,
      Math.min(snappedY, dimensions.height - this.cardHeight - this.gridInset),
    );

    return {
      x: snappedX,
      y: snappedY,
    };
  }

  // ============ Private Shared Helpers ============
  private getCurrentContainerDimensions(): { width: number; height: number } {
    const rect = this.getContainerRect();
    return {
      width: Math.max(0, rect.width),
      height: Math.max(0, rect.height),
    };
  }
}
